/* Per-page SEO tags: <title>, description, canonical, robots, Open Graph /
 * Twitter cards and JSON-LD.
 *
 * The site is a single-page app, so without this every route shares the one
 * <title> and <meta name="description"> baked into public/index.html.
 * applySeo() upserts the tags in <head> imperatively. <head> is outside
 * React's root, so the same code serves two moments: client-side navigation
 * in the browser, and scripts/prerender.mjs snapshotting each route to static
 * HTML for crawlers that do not run JavaScript.
 *
 * Static routes are described in routes.json (titles/descriptions ported from
 * the old WordPress Yoast values where the page existed) and applied by
 * <Seo /> (Seo.tsx). Pages whose content arrives later (/blog/:slug,
 * /countries/:slug, the 404 page) call applySeo() themselves.
 *
 * Build-time switches:
 *   REACT_APP_SITE_ORIGIN  canonical origin, default https://truelog.com.sg —
 *                          beta/preview builds therefore point canonicals at
 *                          production instead of at themselves.
 *   REACT_APP_NOINDEX=1    adds <meta name="robots" content="noindex,nofollow">
 *                          on every page (beta /site3, /site3s).
 */

export const SITE_ORIGIN = (process.env.REACT_APP_SITE_ORIGIN || 'https://truelog.com.sg').replace(/\/+$/, '');
export const SITE_NAME = 'TrueLog';
export const DEFAULT_IMAGE = `${SITE_ORIGIN}/og-image.jpg`;
const LOGO_URL = `${SITE_ORIGIN}/assets/images/truelog-logo.png`;
const NOINDEX_BUILD = process.env.REACT_APP_NOINDEX === '1';
const TITLE_MAX = 70;
const DESCRIPTION_MAX = 160;

export interface SeoInput {
  title: string;
  description?: string;
  /** Route path without the PUBLIC_URL prefix, e.g. "/about-us". Defaults to the current location. */
  path?: string;
  /** Canonical path when this URL is a duplicate of another route. */
  canonical?: string;
  /** Absolute URL or site-relative path of the share image. */
  image?: string;
  type?: 'website' | 'article';
  noindex?: boolean;
  publishedTime?: string;
  modifiedTime?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

export function truncate(text: string, max: number): string {
  const clean = text.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  if (clean.length <= max) return clean;
  const cut = clean.slice(0, max - 1);
  const lastSpace = cut.lastIndexOf(' ');
  return `${cut.slice(0, lastSpace > max - 30 ? lastSpace : cut.length)}…`;
}

export function absoluteUrl(pathOrUrl: string): string {
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  return `${SITE_ORIGIN}${pathOrUrl.startsWith('/') ? '' : '/'}${pathOrUrl}`;
}

/** Collapse trailing slashes; "" → "/". */
export function normalizePath(path: string): string {
  const trimmed = path.replace(/\/+$/, '');
  return trimmed || '/';
}

/** Current pathname with the PUBLIC_URL prefix (e.g. /site3) removed. */
function currentPath(): string {
  const base = (process.env.PUBLIC_URL || '').replace(/\/+$/, '');
  let pathname = window.location.pathname;
  if (base && pathname.startsWith(base)) pathname = pathname.slice(base.length);
  return normalizePath(pathname);
}

function setMeta(attr: 'name' | 'property', key: string, content?: string | null): void {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!content) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setLink(rel: string, href?: string | null): void {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!href) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function setJsonLd(data?: SeoInput['jsonLd']): void {
  const id = 'seo-jsonld';
  let el = document.getElementById(id) as HTMLScriptElement | null;
  if (!data) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement('script');
    el.id = id;
    el.type = 'application/ld+json';
    document.head.appendChild(el);
  }
  // "<" escaped so the JSON can never close the script element early
  el.textContent = JSON.stringify(data).replace(/</g, '\\u003c');
}

export function applySeo(input: SeoInput): void {
  if (typeof document === 'undefined') return;
  const title = truncate(input.title, TITLE_MAX);
  const description = input.description ? truncate(input.description, DESCRIPTION_MAX) : undefined;
  const image = absoluteUrl(input.image || DEFAULT_IMAGE);
  const canonical = input.noindex ? null : absoluteUrl(normalizePath(input.canonical || input.path || currentPath()));
  const type = input.type || 'website';

  document.title = title;
  setMeta('name', 'description', description);
  setMeta('name', 'robots', input.noindex || NOINDEX_BUILD ? 'noindex,nofollow' : null);
  setLink('canonical', canonical);

  setMeta('property', 'og:site_name', SITE_NAME);
  setMeta('property', 'og:locale', 'en_SG');
  setMeta('property', 'og:type', type);
  setMeta('property', 'og:title', title);
  setMeta('property', 'og:description', description);
  setMeta('property', 'og:url', canonical);
  setMeta('property', 'og:image', image);
  setMeta('property', 'article:published_time', type === 'article' ? input.publishedTime : null);
  setMeta('property', 'article:modified_time', type === 'article' ? input.modifiedTime : null);

  setMeta('name', 'twitter:card', 'summary_large_image');
  setMeta('name', 'twitter:title', title);
  setMeta('name', 'twitter:description', description);
  setMeta('name', 'twitter:image', image);

  setJsonLd(input.jsonLd);
}

/* ---------- JSON-LD builders ---------- */

export function organizationJsonLd(): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: `${SITE_ORIGIN}/`,
    logo: LOGO_URL,
    description:
      'Integrated ICT logistics from Singapore: importer and exporter of record, cross-border compliance, global freight and bonded warehousing in 140+ countries.',
    email: 'sales@truelog.com.sg',
    telephone: '+65 6909 3756',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '101 Cecil Street #11-05',
      addressLocality: 'Singapore',
      postalCode: '069533',
      addressCountry: 'SG',
    },
    contactPoint: [
      {
        '@type': 'ContactPoint',
        contactType: 'sales',
        telephone: '+65 6909 3756',
        email: 'sales@truelog.com.sg',
        availableLanguage: ['en'],
      },
    ],
  };
}

export function webSiteJsonLd(): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: `${SITE_ORIGIN}/`,
  };
}

export interface BlogPostingSource {
  title: string;
  slug: string;
  description?: string | null;
  featured_image?: string | null;
  author?: string | null;
  published_at?: string | null;
  updated_at?: string | null;
}

export function blogPostingJsonLd(post: BlogPostingSource): Record<string, unknown> {
  const url = absoluteUrl(`/blog/${post.slug}`);
  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.description ? truncate(post.description, DESCRIPTION_MAX) : undefined,
    image: [post.featured_image ? absoluteUrl(post.featured_image) : DEFAULT_IMAGE],
    datePublished: post.published_at || undefined,
    dateModified: post.updated_at || post.published_at || undefined,
    author: post.author
      ? { '@type': 'Person', name: post.author }
      : { '@type': 'Organization', name: SITE_NAME, url: `${SITE_ORIGIN}/` },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: { '@type': 'ImageObject', url: LOGO_URL },
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    url,
  };
}

export function breadcrumbJsonLd(items: { name: string; path: string }[]): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: absoluteUrl(item.path),
    })),
  };
}
